import '@material/web/icon/icon.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'
import './data-sample-view.js'

import gql from 'graphql-tag'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { connect } from 'pwa-helpers/connect-mixin'
import moment from '@operato/moment-timezone-es'

import { DataGrist, FetchOption, GristRecord } from '@operato/data-grist'
import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { openPopup } from '@operato/layout'
import { PageView, store } from '@operato/shell'
import { CommonGristStyles, CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { isMobileDevice } from '@operato/utils'
import { GhostPrint } from '@operato/ghost-print'

import { p13n } from '@operato/p13n'

@customElement('data-sample-list-page')
export class DataSampleListPage extends connect(store)(p13n(localize(i18next)(PageView))) {
  static styles = [
    ScrollbarStyles,
    CommonGristStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;

        overflow: hidden;

        --grid-header-padding: 2px 0 2px 9px;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() gristConfig: any
  @state() mode: 'CARD' | 'GRID' | 'LIST' = isMobileDevice() ? 'CARD' : 'GRID'

  @query('ox-grist') private grist!: DataGrist

  get context() {
    return {
      title: i18next.t('title.data-sample list'),
      search: {
        handler: (search: string) => {
          this.grist.searchText = search
        },
        value: this.grist?.searchText || ''
      },
      filter: {
        handler: () => {
          this.grist.toggleHeadroom()
        }
      },
      help: 'dataset/data-sample',
      actions: [
        {
          title: i18next.t('button.print'),
          action: this.printSamples.bind(this),
          icon: 'print'
        }
      ],
      exportable: {
        name: i18next.t('title.data-sample list'),
        data: this._exportableData.bind(this)
      },
      toolbar: false
    }
  }

  render() {
    const mode = this.mode || (isMobileDevice() ? 'LIST' : 'GRID')

    return html`
      <ox-grist
        .mode=${mode}
        .config=${this.gristConfig}
        .fetchHandler=${this.fetchHandler.bind(this)}
        .personalConfigProvider=${this.getPagePreferenceProvider('ox-grist')!}
        ?url-params-sensitive=${false /* this.active */}
      >
        <div slot="headroom" class="header">
          <div class="filters">
            <ox-filters-form class="filter" autofocus without-search></ox-filters-form>
          </div>

          <ox-context-page-toolbar class="actions" .context=${this.context}></ox-context-page-toolbar>
        </div>

        <ox-grist-personalizer slot="setting"></ox-grist-personalizer>
      </ox-grist>
    `
  }

  async pageInitialized(lifecycle) {
    const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD')
    const today = moment().format('YYYY-MM-DD')

    this.gristConfig = {
      list: { fields: ['dataSet', 'data', 'updater', 'updatedAt'] },
      columns: [
        { type: 'gutter', gutterName: 'sequence', fixed: true },
        { type: 'gutter', gutterName: 'row-selector', fixed: true, multiple: true },
        {
          type: 'gutter',
          gutterName: 'button',
          fixed: true,
          icon: 'assignment',
          iconOnly: false,
          title: i18next.t('button.detail'),
          width: 72
        },
        {
          type: 'string',
          name: 'name',
          fixed: true,
          label: true,
          header: i18next.t('field.name'),
          record: {
            editable: false
          },
          filter: 'search',
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'description',
          label: true,
          header: i18next.t('field.description'),
          record: {
            editable: false
          },
          filter: 'search',
          width: 150,
          imex: true
        },
        {
          type: 'string',
          name: 'key01',
          header: {
            renderer: i18next.t('field.key-01'),
            group: i18next.t('field.key')
          },
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'key02',
          header: {
            renderer: i18next.t('field.key-02'),
            group: i18next.t('field.key')
          },
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'key03',
          header: {
            renderer: i18next.t('field.key-03'),
            group: i18next.t('field.key')
          },
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'key04',
          header: {
            renderer: i18next.t('field.key-04'),
            group: i18next.t('field.key')
          },
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'key05',
          header: {
            renderer: i18next.t('field.key-05'),
            group: i18next.t('field.key')
          },
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'checkbox',
          name: 'ooc',
          header: i18next.t('field.ooc'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'checkbox',
          name: 'oos',
          header: i18next.t('field.oos'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'json5',
          name: 'data',
          header: i18next.t('field.data'),
          record: {
            editable: false
          },
          width: 200,
          imex: true
        },
        {
          type: 'json5',
          name: 'rawData',
          header: i18next.t('field.raw-data'),
          record: {
            editable: false
          },
          width: 200,
          imex: true
        },
        {
          type: 'string',
          name: 'workDate',
          header: i18next.t('field.work-date'),
          sortable: true,
          filter: {
            type: 'date',
            operator: 'between',
            value: [yesterday, today]
          },
          width: 80,
          imex: true
        },
        {
          type: 'string',
          name: 'workShift',
          header: i18next.t('field.work-shift'),
          sortable: true,
          width: 40,
          imex: true
        },
        {
          type: 'datetime',
          name: 'collectedAt',
          header: i18next.t('field.collected-at'),
          sortable: true,
          width: 180,
          imex: true
        },
        {
          type: 'resource-object',
          name: 'updater',
          header: i18next.t('field.updater'),
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'datetime',
          name: 'updatedAt',
          header: i18next.t('field.updated_at'),
          sortable: true,
          width: 180,
          imex: true
        }
      ],
      rows: {
        appendable: false,
        selectable: {
          multiple: true
        },
        classifier: function (record, rowIndex) {
          var emphasized
          if (record['oos']) {
            emphasized = ['#8B0000', 'white']
          } else if (record['ooc']) {
            emphasized = ['#FF6B6B', 'white']
          }

          return {
            emphasized
          }
        },
        handlers: {
          click: (columns, data, column, record, rowIndex) => {
            openPopup(
              html`
                <data-sample-view data-sample-id=${record.id} style="background-color: white;"></data-sample-view>
              `,
              {
                backdrop: true,
                size: 'large',
                title: i18next.t('title.data-sample view')
              }
            )
          }
        }
      },
      sorters: [
        {
          name: 'collectedAt',
          desc: true
        }
      ]
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    const response = await client.query({
      query: gql`
        query ($filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!]) {
          responses: dataSamples(filters: $filters, pagination: $pagination, sortings: $sortings) {
            items {
              id
              name
              description
              useCase
              key01
              key02
              key03
              key04
              key05
              data
              rawData
              ooc
              oos
              judgment
              workDate
              workShift
              updater {
                id
                name
              }
              updatedAt
              collectedAt
            }
            total
          }
        }
      `,
      variables: {
        filters,
        pagination: { page, limit },
        sortings
      }
    })

    return {
      total: response.data.responses.total || 0,
      records: response.data.responses.items || []
    }
  }

  _exportableData() {
    let records = [] as GristRecord[]
    if (this.grist.selected && this.grist.selected.length > 0) {
      records = this.grist.selected
    } else {
      records = this.grist.data.records
    }

    var headerSetting = this.grist.compiledConfig.columns
      .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
      .map(column => {
        return column.imex === true
          ? {
              header: column.header.renderer(column),
              key: column.name,
              width: column.width,
              type: column.type
            }
          : column.imex
      })

    var data = records.map(item => {
      return {
        id: item.id,
        ...this.gristConfig.columns
          .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
          .reduce((record, column) => {
            const key = column.imex === true ? column.name : column.imex.key
            record[key] = key
              .split('.')
              .reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), item)
            return record
          }, {})
      }
    })

    return { header: headerSetting, data: data }
  }

  printSamples() {
    const selected = this.grist.selected
    const ids = selected.map(record => record.id).join('|')

    GhostPrint.print({
      src: `./data-samples?ids=${ids}`
      // pending: 200 * selected.length
    })
  }
}
